import { VIDEO_UPLOAD_STATUS } from "@repo/shared";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";

import { acquireSocket, releaseSocket } from "~/api/socket";
import { useToast } from "~/components/ui/use-toast";

import type { VideoUploadStatus } from "@repo/shared";

interface UploadNotification {
  uploadId: string;
  status: VideoUploadStatus;
  fileKey?: string;
  fileUrl?: string;
  error?: string;
}

/**
 * @param enabled Sólo quien sube videos necesita esto. Estaba montado en el layout raíz
 * sin condición, así que un visitante anónimo de la home pública descargaba
 * `socket.io-client` (41 KB) y abría una conexión WebSocket permanente para enterarse de
 * que terminó de procesarse un video que subió un administrador.
 */
export function useGlobalVideoUploadNotifications(enabled = true) {
  const { toast } = useToast();
  const { t } = useTranslation();
  useEffect(() => {
    if (!enabled) return;

    const socket = acquireSocket();

    const handleConnect = () => {
      socket.emit("join:user");
    };

    socket.on("connect", handleConnect);
    socket.connect();

    if (socket.connected) {
      handleConnect();
    }

    const handleUploadStatusChange = (notification: UploadNotification) => {
      match(notification.status)
        .with(VIDEO_UPLOAD_STATUS.PROCESSED, () =>
          toast({
            description: t("uploadFile.toast.videoReady"),
          }),
        )
        .with(VIDEO_UPLOAD_STATUS.FAILED, () =>
          toast({
            description: t("uploadFile.toast.videoFailed"),
          }),
        );
    };
    socket.on("upload-status-change", handleUploadStatusChange);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("upload-status-change", handleUploadStatusChange);
      releaseSocket();
    };
  }, [enabled, toast, t]);
}
